import { motion } from "framer-motion";
import {
  ComposedChart, Area, Bar, BarChart, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import { Disc3, Eye, CalendarDays, Users, Film, Flame, Info, ExternalLink } from "lucide-react";
import { thumbUrl } from "@/lib/api";
import { StatCard } from "@/components/StatCard";
import { fmtFull, fmtShort, fmtDate } from "@/lib/format";

const LEVELS = {
  Low: { cls: "bg-neutral-100 text-neutral-700 border-neutral-300", bars: 1 },
  Mid: { cls: "bg-[#E6F0F2] text-[#35606E] border-[#9DC0C8]", bars: 2 },
  Max: { cls: "bg-[#35606E] text-white border-[#35606E]", bars: 3 },
};
const dash = (v, f = fmtFull) => (v == null ? "—" : f(v));
const tick = (d) => fmtDate(d).replace(/, \d{4}$/, "");

function LevelBadge({ level }) {
  const s = LEVELS[level] || LEVELS.Low;
  return (
    <div data-testid="exposure-level" className={`flex items-center gap-3 rounded-2xl border px-4 py-3 ${s.cls}`}>
      <div className="flex items-end gap-1">
        {[1, 2, 3].map((i) => (
          <span key={i} className={`w-2 rounded-sm ${i <= s.bars ? "bg-current" : "bg-current opacity-20"}`} style={{ height: 8 + i * 6 }} />
        ))}
      </div>
      <div>
        <p className="text-[10px] font-semibold uppercase tracking-wider opacity-80">Exposure level</p>
        <p className="font-display text-2xl font-extrabold leading-none">{level}{level === "Max" ? " exposure" : ""}</p>
      </div>
    </div>
  );
}

function DailyTip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const row = payload[0].payload;
  return (
    <div className="rounded-xl border border-black/10 bg-white px-3 py-2 text-sm shadow-lg">
      <p className="mb-1 text-xs font-medium text-neutral-500">{fmtDate(label)}</p>
      {row.cumulative_views != null && <p className="font-semibold text-[#35606E]">Total views: {fmtFull(row.cumulative_views)}</p>}
      <p className="font-semibold text-[#C77D2E]">
        Daily gain: {row.daily_gain == null ? (row.correction ? "data correction" : "no data") : fmtFull(row.daily_gain)}
      </p>
      {row.estimated && <p className="text-xs text-neutral-500">Averaged across a gap between snapshots</p>}
      {row.posts > 0 && <p className="text-xs text-neutral-600">{row.posts} post{row.posts === 1 ? "" : "s"} submitted</p>}
    </div>
  );
}

function GrowthChart({ data }) {
  return (
    <div className="pb-card mt-6 p-6">
      <h3 className="font-display text-lg font-bold">Growth since campaign start</h3>
      <p className="text-sm text-neutral-500">Total views (line) and daily views gained (bars), from stored snapshots</p>
      <div className="mt-5 h-72 w-full" data-testid="exposure-growth-chart">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={data} margin={{ top: 5, right: 8, left: 8, bottom: 0 }}>
            <CartesianGrid vertical={false} stroke="#00000008" />
            <XAxis dataKey="date" tickFormatter={tick} tick={{ fontSize: 11, fill: "#9ca3af" }} axisLine={false} tickLine={false} minTickGap={30} />
            <YAxis yAxisId="c" tickFormatter={fmtShort} tick={{ fontSize: 11, fill: "#9ca3af" }} axisLine={false} tickLine={false} width={44} />
            <YAxis yAxisId="d" orientation="right" tickFormatter={fmtShort} tick={{ fontSize: 11, fill: "#9ca3af" }} axisLine={false} tickLine={false} width={44} />
            <Tooltip content={<DailyTip />} />
            <Bar yAxisId="d" dataKey="daily_gain" name="Daily gain" fill="#E9B97A" radius={[3, 3, 0, 0]} />
            <Area yAxisId="c" type="monotone" dataKey="cumulative_views" name="Total views" stroke="#35606E" strokeWidth={2.5} fill="#35606E" fillOpacity={0.08} connectNulls />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function PostsChart({ data, undated }) {
  return (
    <div className="pb-card mt-6 p-6">
      <h3 className="font-display text-lg font-bold">Posts submitted per day</h3>
      <p className="text-sm text-neutral-500">By each post's posted date</p>
      {undated > 0 && <p data-testid="exposure-undated-note" className="mt-1 text-xs text-neutral-500">{fmtFull(undated)} post{undated === 1 ? "" : "s"} without a known posted date (import timestamp only) {undated === 1 ? "is" : "are"} left out of this chart.</p>}
      <div className="mt-5 h-48 w-full" data-testid="exposure-posts-chart">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 5, right: 8, left: 8, bottom: 0 }}>
            <CartesianGrid vertical={false} stroke="#00000008" />
            <XAxis dataKey="date" tickFormatter={tick} tick={{ fontSize: 11, fill: "#9ca3af" }} axisLine={false} tickLine={false} minTickGap={30} />
            <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#9ca3af" }} axisLine={false} tickLine={false} width={32} />
            <Tooltip content={<DailyTip />} />
            <Bar dataKey="posts" name="Posts" fill="#35606E" radius={[3, 3, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function ViralCallout({ viral }) {
  const found = viral.status === "found";
  return (
    <div data-testid="exposure-viral" className={`pb-card mt-6 flex gap-4 p-6 ${found ? "border-[#E9B97A] bg-[#FFF7EC]" : ""}`}>
      <Flame className={`mt-0.5 h-6 w-6 shrink-0 ${found ? "text-[#C77D2E]" : "text-neutral-300"}`} />
      <div>
        <h3 className="font-display text-lg font-bold">Viral window</h3>
        {found ? (
          <>
            {viral.windows.map((w) => (
              <p key={w.start} data-testid={`exposure-viral-window-${w.start}`} className="mt-1 text-sm">
                <span className="font-semibold">{fmtDate(w.start)}{w.end !== w.start ? ` – ${fmtDate(w.end)}` : ""}</span>
                {" · "}{fmtFull(w.views)} views gained (peak {fmtFull(w.peak)}/day)
              </p>
            ))}
            <p className="mt-2 text-xs text-neutral-500">
              A day stands out when it gains at least 2.5× this campaign's typical day ({fmtFull(viral.baseline_per_day)}/day) and at least 1,000 views — threshold {fmtFull(viral.threshold_per_day)}/day.
            </p>
          </>
        ) : (
          <p data-testid="exposure-viral-note" className="mt-1 text-sm text-neutral-600">
            {viral.note}
            {viral.status === "none" && ` (typical day: ${fmtFull(viral.baseline_per_day)} views; threshold ${fmtFull(viral.threshold_per_day)}/day).`}
          </p>
        )}
        <p className="mt-1 text-xs text-neutral-400">Days averaged across a gap between snapshots are never flagged. A jump can also come from posts being added to the campaign that day.</p>
      </div>
    </div>
  );
}

function TopPost({ post, share }) {
  if (!post) return null;
  return (
    <a href={post.post_url} target="_blank" rel="noreferrer" data-testid="exposure-top-post" className="pb-card mt-6 flex items-center gap-4 p-5 transition-colors hover:bg-neutral-50">
      <div className="h-16 w-16 shrink-0 overflow-hidden rounded-xl bg-neutral-900">
        {post.thumbnail ? <img src={thumbUrl(post.thumbnail, 160)} alt="" className="h-full w-full object-cover" /> : <div className="grid h-full place-items-center text-neutral-600"><Film className="h-6 w-6" /></div>}
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-xs font-semibold uppercase tracking-wide text-[#6E97A2]">Top post</p>
        <p className="truncate font-semibold">@{post.creator_handle}</p>
        <p className="text-sm text-neutral-500">{fmtFull(post.views)} views</p>
      </div>
      <div className="text-right">
        <p data-testid="exposure-top-post-share" className="num-display text-2xl font-extrabold">{dash(share, (v) => `${v}%`)}</p>
        <p className="text-[11px] text-neutral-500">of total views</p>
      </div>
      <ExternalLink className="h-4 w-4 shrink-0 text-neutral-400" />
    </a>
  );
}

function Header({ d }) {
  const status = d.campaign.status === "complete" ? "Complete" : d.campaign.status === "in_progress" ? "In progress" : d.campaign.status || "—";
  const cover = d.campaign.cover_art || d.song?.cover_art || d.artist?.image;
  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="pb-card flex flex-col gap-5 p-6 lg:flex-row lg:items-center" data-testid="exposure-header">
      <div className="h-24 w-24 shrink-0 overflow-hidden rounded-2xl bg-neutral-900">
        {cover ? <img src={thumbUrl(cover, 240)} alt="" className="h-full w-full object-cover" /> : <div className="grid h-full place-items-center text-neutral-600"><Disc3 className="h-8 w-8" /></div>}
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-xs font-semibold uppercase tracking-wide text-[#6E97A2]">Exposure Check</p>
        <h1 data-testid="exposure-title" className="font-display text-3xl font-extrabold tracking-tight">
          {d.song?.title || d.campaign.title} <span className="text-neutral-400">— {d.artist?.name || d.campaign.artist}</span>
        </h1>
        <p className="mt-1 text-sm text-neutral-500" data-testid="exposure-meta">
          {d.campaign.title} · Launched {fmtDate(d.launch_date)} · {status} · {d.post_count} posts · {d.creator_count == null ? "creators not recorded" : `${d.creator_count} creators`}
        </p>
      </div>
      <LevelBadge level={d.level} />
    </motion.div>
  );
}

function RuleBox({ d }) {
  return (
    <div className="pb-card mt-6 p-6" data-testid="exposure-rule">
      <div className="flex items-center gap-2"><Info className="h-4 w-4 text-[#35606E]" /><h3 className="font-display text-lg font-bold">How the level is earned</h3></div>
      <ul className="mt-3 space-y-1.5 text-sm text-neutral-700">
        {d.rule.map((r) => <li key={r}>• {r}</li>)}
      </ul>
      <p className="mt-3 text-xs text-neutral-500">
        This campaign: {dash(d.total_views)} total views · {d.momentum_per_day == null ? "momentum unknown (no recent snapshots)" : `${fmtFull(d.momentum_per_day)} views/day over the last 7 days`} → <span className="font-semibold">{d.level}</span>.
      </p>
    </div>
  );
}

export function ExposureReport({ data: d }) {
  return (
    <div data-testid="exposure-report">
      <Header d={d} />
      <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
        <StatCard testId="exposure-total-views" size="lg" label="Total campaign views" value={dash(d.total_views, fmtShort)} sub={`${dash(d.total_views)} views`} icon={Eye} index={0} />
        <StatCard testId="exposure-avg-per-day" size="lg" label="Average per day" value={dash(d.avg_views_per_day, fmtShort)} sub={`over ${d.span_days} day${d.span_days === 1 ? "" : "s"} since launch`} icon={CalendarDays} index={1} />
        <StatCard testId="exposure-momentum" size="lg" label="Last 7 days, per day" value={dash(d.momentum_per_day, fmtShort)} sub={d.momentum_per_day == null ? "no snapshot in the last 7 days" : "from stored snapshots"} icon={Users} index={2} />
      </div>
      <TopPost post={d.top_post} share={d.top_post_share} />
      <GrowthChart data={d.daily} />
      <p data-testid="exposure-span-note" className="mt-2 px-1 text-xs text-neutral-500">
        Span: {fmtDate(d.daily[0]?.date)} – {fmtDate(d.today)} ({d.snapshot_days} snapshot day{d.snapshot_days === 1 ? "" : "s"}{d.last_snapshot_date ? `, latest ${fmtDate(d.last_snapshot_date)}` : ""}). Pre-campaign history (back to the song's release) is not tracked, so growth starts at campaign start.
      </p>
      <PostsChart data={d.daily} undated={d.undated_posts} />
      <ViralCallout viral={d.viral} />
      <RuleBox d={d} />
    </div>
  );
}
